import { Button, Checkbox, Form, Input, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import checkEmailAddress from '../../../lib/checkEmailAddress';
import useAnalyticsContext from '../../../lib/hooks/useAnalyticsContext';
import useReturnUrl from '../../../lib/hooks/useReturnUrl';
import translateServerError from '../../../lib/translateServerError';
import { detectCountryCode, noop, testEmail } from '../../../lib/utils';
import { VALIDATION_TIMEOUT } from '../../constants';
import ANALYTICS_EVENTS from '../../constants/analyticsEvents';
import { LoginStatus } from '../../reducers/login';
import FormSubHeader from '../FormSubHeader';
import PostalCodeInput from '../PostalCodeInput';
import styles from './SignupForm.module.css';

const cx = classnames.bind(styles);

export default function SignupForm({
  email: defaultEmail,
  errorMessage,
  loginStatus,
  onTermsChange,
  partner,
  signedRequest,
  signup,
  socialNetwork,
}) {
  const { t } = useTranslation(['auth', 'errors']);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(undefined);
  const [showServerEmailError, setShowServerEmailError] = useState(true);
  const [postal, setPostal] = useState('');
  const [postalError, setPostalError] = useState(undefined);
  const [countryCode, setCountryCode] = useState(undefined);
  const [emailPrefsSelected, setEmailPrefsSelected] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsError, setTermsError] = useState(undefined);
  const analytics = useAnalyticsContext();
  const returnUrl = useReturnUrl();

  useEffect(() => {
    analytics.tagPageView({ name: ANALYTICS_EVENTS.IAM_Account_Signup });
  }, [analytics]);

  useEffect(() => {
    if (defaultEmail) {
      setEmail(defaultEmail);
    }
  }, [defaultEmail]);

  // This should only be called when the country code is about to update, as we are setting a default value for
  // the email preferences checkbox when we do so: USA = true, rest = false
  const handleCountryCodeUpdate = (postalCode) => {
    const newCountryCode = detectCountryCode(postalCode);
    setEmailPrefsSelected(newCountryCode === 'USA');
    setCountryCode(newCountryCode);
  };

  const onEmailBlur = useCallback(async () => {
    const emailSuggestion = await checkEmailAddress(email);

    if (emailSuggestion) {
      setEmailError(t('signupForm.emailSuggestion', { ns: 'errors', suggestion: emailSuggestion }));
    }
  }, [email, t]);

  const onEmailChange = useCallback(({ target }) => {
    setEmail(target.value);
    setEmailError(undefined);
    setShowServerEmailError(false);
  }, []);

  const onPostalChange = useCallback(({ target }) => {
    setPostal(target.value);
    setPostalError(undefined);
  }, []);

  const onEmailPrefsChange = useCallback(() => {
    setEmailPrefsSelected(!emailPrefsSelected);
  }, [emailPrefsSelected]);

  const handleTermsChange = useCallback(() => {
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.IAM_Terms_Checkbox_Clicked,
      attributes: {
        checked: !termsChecked,
      },
    });
    setTermsChecked(!termsChecked);
    setTermsError(undefined);
    onTermsChange(!termsChecked);
  }, [analytics, onTermsChange, termsChecked]);

  const handleTermsLinkClick = useCallback(() => {
    analytics.tagEvent({ name: ANALYTICS_EVENTS.IAM_Terms_Link_Clicked });
  }, [analytics]);

  useEffect(() => {
    const timerId = setTimeout(() => { handleCountryCodeUpdate(postal); }, VALIDATION_TIMEOUT);
    return () => clearTimeout(timerId);
  }, [postal]);

  useEffect(() => {
    // NOTE: if we had an error on this screen, then jumped to /login, then came back, our status would still be failed
    // but we'd have no content in the fields. Therefore, make sure we don't show an error message on an empty field.
    if (loginStatus === LoginStatus.Failed && !!email) {
      if (errorMessage && showServerEmailError) {
        // if we have an error message from the reducer, it'll only be because of an email address issue
        // the postal code validation issues are handled by the PostalCodeInput validation onSignUpClick
        setEmailError(translateServerError(errorMessage, t));
      }
      return;
    }

    if (loginStatus === LoginStatus.InProgress) {
      setEmailError(translateServerError(errorMessage, t));
      setPostalError(translateServerError(errorMessage, t));
    }
  }, [email, errorMessage, loginStatus, showServerEmailError, t]);

  const onSignUpClick = (e) => {
    e.preventDefault();
    let validEmail = testEmail(email);
    const validPostalCode = !!countryCode;

    if (!validEmail) {
      setEmailError(t('signupForm.emailInvalid', { ns: 'errors' }));
    }

    const [prefix, fullDomain] = email.split('@');
    if (prefix.length > 30 || prefix.length < 1) {
      setEmailError(t('signupForm.emailPrefixLength', { ns: 'errors' }));
      validEmail = false;
    }

    if (fullDomain) {
      const [domain, extension] = fullDomain.split('.');
      if (fullDomain.length > 50 || domain.length < 1) {
        setEmailError(t('signupForm.emailDomainLength', { ns: 'errors' }));
        validEmail = false;
      }

      if (!extension || extension?.length < 1) {
        setEmailError(t('signupForm.emailDomainExtension', { ns: 'errors' }));
        validEmail = false;
      }
    }

    if (!validPostalCode) {
      setPostalError(t('signupForm.postalCodeInvalid', { ns: 'errors' }));
    }

    if (!termsChecked) {
      setTermsError(t('signupForm.termsRequired', { ns: 'errors' }));
    }

    if (!validEmail || !validPostalCode || !termsChecked) {
      return;
    }

    setShowServerEmailError(true);

    analytics.tagEvent({ name: ANALYTICS_EVENTS.IAM_Signup_Clicked });

    signup(partner, {
      country: countryCode,
      email,
      postalCode: postal,
      returnUrl,
      signedRequest,
      socialNetwork,
      subscribeChecked: emailPrefsSelected,
      termsChecked,
    });
  };

  return (
    <React.Fragment>
      <FormSubHeader>
        {t('signupForm.subheader', { ns: 'auth' })}
      </FormSubHeader>
      <Form onSubmit={onSignUpClick} method="POST" className={cx('form')}>
        <div className={cx('formGroup')}>
          <Input
            id="email"
            name="email"
            data-testid="emailInput"
            label={t('signupForm.fields.email', { ns: 'auth' })}
            error={emailError}
            value={email}
            onBlur={onEmailBlur}
            onChange={onEmailChange}
            autoComplete="username"
          />
        </div>
        {!!emailError && <br />}
        <div className={cx('formGroup')}>
          <PostalCodeInput
            id="postalcode"
            data-testid="postalCodeInput"
            name="postalCode"
            label={t('signupForm.fields.postalCode', { ns: 'auth' })}
            error={postalError}
            value={postal}
            onChange={onPostalChange}
          />
        </div>
        {!!postalError && <br />}
        {(countryCode === 'CAN' || countryCode === 'AUS') && (
          <div className={cx('checkboxWrapper')}>
            <Checkbox
              id="email_prefs_checkbox"
              name="subscribeChecked"
              label={t('signupForm.emailPrefs', { ns: 'auth' })}
              checked={emailPrefsSelected}
              onChange={onEmailPrefsChange}
            />
          </div>
        )}
        <div className={cx('checkboxWrapper')}>
          <Checkbox
            id="terms_checkbox"
            name="termsChecked"
            checked={termsChecked}
            onChange={handleTermsChange}
            label={(
              <Text className={cx('disclaimer')}>
                <Trans
                  i18nKey="signupForm.terms"
                  ns="auth"
                >
                  I agree to GasBuddy’s <a href="https://www.gasbuddy.com/disclaimer" rel="noopener noreferrer" target="_blank" onClick={handleTermsLinkClick} className={cx('textLink')}> Terms&nbsp;and&nbsp;Conditions</a><sup>*</sup>
                </Trans>
              </Text>
            )}
          />
        </div>
        <br />
        <Button
          className={cx('submit')}
          disabled={!termsChecked}
          primary
          fluid
          type="submit"
        >
          {t('signupForm.button', { ns: 'auth' })}
        </Button>
        {!!termsError && (
          <Text color="orange">{termsError}</Text>
        )}
      </Form>
    </React.Fragment>
  );
}

SignupForm.propTypes = {
  email: PropTypes.string,
  errorMessage: PropTypes.string,
  loginStatus: PropTypes.number,
  onTermsChange: PropTypes.func,
  partner: PropTypes.string,
  signedRequest: PropTypes.string,
  signup: PropTypes.func.isRequired,
  socialNetwork: PropTypes.number,
};

SignupForm.defaultProps = {
  email: undefined,
  errorMessage: undefined,
  loginStatus: undefined,
  onTermsChange: noop,
  partner: undefined,
  signedRequest: undefined,
  socialNetwork: undefined,
};
